/**
 * 注册服务
 */

export interface SimpleRegisterRequest {
    wechatOpenId: string;
    companyName: string;
    businessLicenseNumber: string;
    contactName: string;
    contactPhone: string;
    contactEmail?: string;
    contactPosition?: string;
    businessLicenseImageUrl?: string;
}

export interface SimpleRegisterResponse {
    success: boolean;
    message?: string;
    userProfile?: any;
}

/**
 * 检查登录状态
 */
export const checkLoginStatus = async (
    wechatOpenId: string
): Promise<{ success: boolean; hasProfile: boolean; message?: string; userProfile?: any }> => {
    try {
        const apiUrl = '/auth/check-login';

        const response = await fetch(`${apiUrl}?wechatOpenId=${encodeURIComponent(wechatOpenId)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        console.log('检查登录状态响应:', response.status, response.statusText);

        if (!response.ok) {
            throw new Error(`检查登录状态失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('登录状态结果:', result);

        return {
            success: result.success,
            hasProfile: result.hasProfile,
            message: result.message,
            userProfile: result.userProfile
        };
    } catch (error) {
        console.error('检查登录状态错误:', error);
        return {
            success: false,
            hasProfile: false,
            message: error instanceof Error ? error.message : '检查登录状态失败',
        };
    }
};

/**
 * 简单注册API调用 - 提交用户档案
 */
export const simpleRegister = async (
    registerData: SimpleRegisterRequest
): Promise<SimpleRegisterResponse> => {
    try {
        // 适配 Nginx 配置：
        // Nginx: /api/ -> http://127.0.0.1:7081/ (去掉 /api)
        // 所以前端请求 /api/api/xxx 经过 Nginx 后变成 /api/xxx
        const apiUrl = '/api/user-profile/submit';

        // 构建请求数据，按照新API格式
        const requestData = {
            wechatOpenId: registerData.wechatOpenId,
            companyName: registerData.companyName,
            businessLicenseNumber: registerData.businessLicenseNumber,
            contactName: registerData.contactName,
            contactPhone: registerData.contactPhone,
            contactEmail: registerData.contactEmail || '',
            contactPosition: registerData.contactPosition || '',
            businessLicenseImageUrl: registerData.businessLicenseImageUrl || '',
        };

        console.log('发送用户档案提交请求:', { url: apiUrl, data: requestData });

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        });

        console.log('提交响应状态:', response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('提交失败响应:', {
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                body: errorText
            });

            if (response.status === 400) {
                throw new Error('提交的信息有误，请检查后重试');
            } else if (response.status === 404) {
                throw new Error('接口不存在，请联系技术支持');
            } else if (response.status >= 500) {
                throw new Error('服务器内部错误，请稍后重试');
            } else {
                throw new Error(`提交失败: ${response.status} ${response.statusText}`);
            }
        }

        const result = await response.json();
        console.log('提交响应:', result);

        // 解析响应
        if (result.success) {
            return {
                success: true,
                userProfile: result.userProfile,
                message: result.message || '提交成功',
            };
        } else {
            return {
                success: false,
                message: result.message || '提交失败',
            };
        }
    } catch (error) {
        console.error('提交请求错误:', error);

        // 如果是网络错误，提供更明确的提示
        if (error instanceof TypeError && error.message.includes('fetch')) {
            return {
                success: false,
                message: '网络错误，请检查网络连接后重试',
            };
        }

        return {
            success: false,
            message: error instanceof Error ? error.message : '提交失败，请稍后重试',
        };
    }
};
